import { useEffect, useMemo, useState, Fragment } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, AlertTriangle, BookOpen, Clock, Download, Filter, Loader2, MessageCircleQuestion, Phone, RefreshCw, Search, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { fmtDuration, fmtDate, StatTile, ActivityHeatmap, SimLogCard } from "@/pages/audit/AuditParts";
import { DrillDialog, HeatmapDrillDialog, CheckinDialog } from "@/pages/audit/AuditDialogs";

const LATE_NIGHT_FLAG_THRESHOLD = 3;

// The set of classes the school supports, mirrored from the backend
// ALL_CLASSES constant in admin_subjects.py. Hard-coded so the filter is
// instant and works even when no sessions exist yet.
const ALL_CLASSES = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

const RANGES = [
  { value: "today", label: "Today" },
  { value: "week", label: "Last 7 days" },
  { value: "month", label: "Last 30 days" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom range" },
];

export default function AdminAudit({ embedded } = {}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isTeacher = user?.role === "teacher";

  // For teachers, lock the available classes + sections to what they teach.
  // Admin sees the full school roster (ALL_CLASSES + all sections).
  const teacherClasses = useMemo(() => {
    if (!isTeacher) return null;
    return [...new Set((user?.assignments || []).map((a) => a.class_name).filter(Boolean))];
  }, [isTeacher, user]);
  const teacherSections = useMemo(() => {
    if (!isTeacher) return null;
    return [...new Set((user?.assignments || []).map((a) => a.section).filter(Boolean))];
  }, [isTeacher, user]);

  const [range, setRange] = useState("week");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [section, setSection] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [data, setData] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classOpts] = useState(ALL_CLASSES);
  const [allSubjects, setAllSubjects] = useState([]); // full {id,name,class_name}[] for narrowing
  const [drillStudent, setDrillStudent] = useState(null); // {student_id, student_name, ...}
  const [drillSessions, setDrillSessions] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  // Heatmap cell drill-down — sessions in one weekday×hour slot.
  const [hmDrill, setHmDrill] = useState(null);   // {weekday, hour, label}
  const [hmSessions, setHmSessions] = useState([]);
  const [hmDrillLoading, setHmDrillLoading] = useState(false);
  // student_id -> number of late-night (10pm–5am) sessions in the current range.
  const [lateNightCounts, setLateNightCounts] = useState({});
  // student_id -> { last_at, status, by } of the latest wellbeing check-in call.
  const [checkinLog, setCheckinLog] = useState({});
  // Wellbeing check-in parent call.
  const [checkinTarget, setCheckinTarget] = useState(null);  // student row obj
  const [checkinMsg, setCheckinMsg] = useState("");
  const [checkinSending, setCheckinSending] = useState(false);
  // Client-side search to quickly find a single student inside the loaded
  // summary table. Matches against name (case-insensitive) AND roll number.
  const [searchQuery, setSearchQuery] = useState("");

  // Admin loads the canonical subject list from /admin/subjects on mount.
  // Teachers don't have that privilege, so we derive their subject filter
  // from whatever rows the audit summary returns (each row already exposes a
  // `subjects[]` array with {subject_id, subject_name}).
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/subjects");
        if (!cancelled) setAllSubjects(data || []);
      } catch {/* harmless */}
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  // When the admin picks a class, narrow the subject dropdown to that class.
  // Selecting "All classes" restores the full subject list.
  const subjectOpts = useMemo(() => {
    if (!classFilter || classFilter === "all") return allSubjects;
    return allSubjects.filter((s) => String(s.class_name) === String(classFilter));
  }, [allSubjects, classFilter]);

  // If the currently-selected subject doesn't belong to the new class, reset it
  // so the table doesn't show "no rows" because of a stale filter.
  useEffect(() => {
    if (subjectFilter === "all") return;
    const ok = subjectOpts.some((s) => s.id === subjectFilter);
    if (!ok) setSubjectFilter("all");
  }, [subjectOpts, subjectFilter]);

  const reload = async () => {
    setLoading(true);
    try {
      const params = { range };
      if (range === "custom") { params.start = start; params.end = end; }
      if (classFilter && classFilter !== "all") params.class_name = classFilter;
      if (section && section !== "all") params.section = section;
      if (subjectFilter && subjectFilter !== "all") params.subject_id = subjectFilter;
      const { data: res } = await api.get("/audit/tuition", { params });
      setData(res);
      try {
        const { data: hm } = await api.get("/audit/tuition/heatmap", { params });
        setHeatmap(hm);
      } catch { setHeatmap(null); }
      // Tally late-night (10pm–5am) sessions per student for the "check-in" flag.
      try {
        const { data: ln } = await api.get("/audit/tuition/heatmap/sessions", {
          params: { ...params, hours: "22,23,0,1,2,3,4" },
        });
        const counts = {};
        for (const s of (ln.sessions || [])) {
          if (s.student_id) counts[s.student_id] = (counts[s.student_id] || 0) + 1;
        }
        setLateNightCounts(counts);
      } catch { setLateNightCounts({}); }
      // Wellbeing check-in contact log (auditable record per student).
      try {
        const { data: cl } = await api.get("/audit/tuition/checkin-log");
        setCheckinLog(cl.by_student || {});
      } catch { setCheckinLog({}); }
      // Teacher subject filter — derive from the rows we just received.
      if (!isAdmin) {
        const seen = new Map();
        for (const s of res.students || []) {
          for (const sub of s.subjects || []) {
            if (sub.subject_id) seen.set(sub.subject_id, { id: sub.subject_id, name: sub.subject_name, class_name: s.class_name });
          }
        }
        setAllSubjects([...seen.values()]);
      }
    } catch {
      setData({ students: [], totals: { students_active: 0, total_seconds: 0, total_questions: 0, total_sessions: 0 } });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [range, classFilter, section, subjectFilter]);

  const openDrill = async (student) => {
    setDrillStudent(student);
    setDrillLoading(true);
    setDrillSessions([]);
    try {
      const params = { student_id: student.student_id, range };
      if (range === "custom") { params.start = start; params.end = end; }
      const { data: res } = await api.get("/audit/tuition/sessions", { params });
      setDrillSessions(res.sessions || []);
    } catch {/* swallow */}
    finally { setDrillLoading(false); }
  };

  const openHeatCell = async (weekday, hour, wd) => {
    const hr12 = hour % 12 === 0 ? 12 : hour % 12;
    const label = `${wd} ${hr12}${hour < 12 ? "am" : "pm"}`;
    setHmDrill({ weekday, hour, label });
    setHmDrillLoading(true);
    setHmSessions([]);
    try {
      const params = { weekday, hour, range };
      if (range === "custom") { params.start = start; params.end = end; }
      if (classFilter && classFilter !== "all") params.class_name = classFilter;
      if (section && section !== "all") params.section = section;
      if (subjectFilter && subjectFilter !== "all") params.subject_id = subjectFilter;
      const { data: res } = await api.get("/audit/tuition/heatmap/sessions", { params });
      setHmSessions(res.sessions || []);
    } catch {/* swallow */}
    finally { setHmDrillLoading(false); }
  };

  const exportHeatSlot = () => {
    if (!hmSessions.length) return;
    const cols = ["student_name", "roll_number", "class_name", "section", "subject_name", "lesson_title", "started_at", "ended_at", "active_seconds", "question_count"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...hmSessions.map((s) => cols.map((c) => esc(s[c])).join(","))].join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `sessions_${(hmDrill?.label || "slot").replace(/\s+/g, "_")}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const openCheckin = (s) => {
    const first = (s.student_name || "the student").split(" ")[0];
    setCheckinTarget(s);
    setCheckinMsg(
      `Namaste, this is Trishul High School. We noticed that ${first} has been using the learning app quite late at night. We wanted to gently check in and make sure everything is alright at home. Please feel free to reach out to the class teacher if you would like to talk. Thank you.`
    );
  };

  const sendCheckin = async () => {
    if (!checkinTarget) return;
    const msg = checkinMsg.trim();
    if (msg.length < 5) { toast.error("Message is too short"); return; }
    setCheckinSending(true);
    try {
      const { data: res } = await api.post("/announcements/calls", {
        student_ids: [checkinTarget.student_id],
        message: msg,
        category: "wellbeing",
      });
      toast.success(`Wellbeing call queued for ${checkinTarget.student_name}'s parent (${res.queued || 1})`);
      setCheckinLog((prev) => ({
        ...prev,
        [checkinTarget.student_id]: { last_at: new Date().toISOString(), status: "pending" },
      }));
      setCheckinTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not queue the call");
    } finally { setCheckinSending(false); }
  };

  const openLateNight = async () => {
    setHmDrill({ label: "Late-night · 10pm–5am" });
    setHmDrillLoading(true);
    setHmSessions([]);
    try {
      const params = { hours: "22,23,0,1,2,3,4", range };
      if (range === "custom") { params.start = start; params.end = end; }
      if (classFilter && classFilter !== "all") params.class_name = classFilter;
      if (section && section !== "all") params.section = section;
      if (subjectFilter && subjectFilter !== "all") params.subject_id = subjectFilter;
      const { data: res } = await api.get("/audit/tuition/heatmap/sessions", { params });
      setHmSessions(res.sessions || []);
    } catch {/* swallow */}
    finally { setHmDrillLoading(false); }
  };

  const exportCsv = () => {
    const params = new URLSearchParams({ range });
    if (range === "custom") { params.set("start", start); params.set("end", end); }
    if (classFilter && classFilter !== "all") params.set("class_name", classFilter);
    if (section && section !== "all") params.set("section", section);
    if (subjectFilter && subjectFilter !== "all") params.set("subject_id", subjectFilter);
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/audit/tuition/export?${params}`;
    // Use a temporary anchor to inherit the auth cookie / token via the api interceptor pattern.
    api.get(`/audit/tuition/export?${params}`, { responseType: "blob" })
      .then((r) => {
        const blob = new Blob([r.data], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `tuition_audit_${range}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(() => { window.open(url, "_blank"); });
  };

  // Apply the client-side search filter so the Top-3 callouts and the
  // per-student table react instantly without re-fetching.
  const filteredStudents = useMemo(() => {
    const list = data?.students || [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) =>
      (s.student_name || "").toLowerCase().includes(q) ||
      (s.roll_number || "").toLowerCase().includes(q)
    );
  }, [data, searchQuery]);

  const top = useMemo(() => filteredStudents.slice(0, 3), [filteredStudents]);

  const Wrap = embedded ? Fragment : Layout;

  return (
    <Wrap>
    <div className="space-y-6 max-w-7xl" data-testid="admin-audit-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Activity className="h-7 w-7 text-sky-500" /> Activity Audit
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Which student spent how much active time on which subject in the AI Tutor — and how many questions they asked.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={reload} disabled={loading} className="rounded-full" data-testid="audit-refresh-btn">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            <span className="ml-1">Refresh</span>
          </Button>
          <Button size="sm" className="rounded-full bg-emerald-500 hover:bg-emerald-600" onClick={exportCsv} data-testid="audit-export-csv-btn">
            <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {user?.is_dev_admin && <SimLogCard />}

      {/* Filter bar */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
          <Filter className="h-3.5 w-3.5" /> Filters
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Range</div>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="rounded-xl" data-testid="audit-range-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {range === "custom" && (
            <>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">From</div>
                <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-xl" data-testid="audit-start-input" />
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">To</div>
                <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-xl" data-testid="audit-end-input" />
              </div>
            </>
          )}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Class</div>
            <Select value={classFilter} onValueChange={setClassFilter}>
              <SelectTrigger className="rounded-xl" data-testid="audit-class-select"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isTeacher ? "My classes" : "All classes"}</SelectItem>
                {(isTeacher ? (teacherClasses || []) : classOpts).map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Section</div>
            <Select value={section} onValueChange={setSection}>
              <SelectTrigger className="rounded-xl" data-testid="audit-section-select"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isTeacher ? "My sections" : "All sections"}</SelectItem>
                {(isTeacher ? (teacherSections || []) : ["A", "B", "C", "D"]).map((s) => <SelectItem key={s} value={s}>Section {s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Subject</div>
            <Select value={subjectFilter} onValueChange={setSubjectFilter}>
              <SelectTrigger className="rounded-xl" data-testid="audit-subject-select"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {subjectOpts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.class_name ? ` · Class ${s.class_name}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile icon={Sparkles} label="Active students" value={data?.totals?.students_active ?? 0} tone="sky" testid="audit-total-students" />
        <StatTile icon={Clock} label="Total active time" value={fmtDuration(data?.totals?.total_seconds || 0)} tone="emerald" testid="audit-total-time" />
        <StatTile icon={MessageCircleQuestion} label="Questions asked" value={data?.totals?.total_questions ?? 0} tone="violet" testid="audit-total-questions" />
        <StatTile icon={BookOpen} label="Lesson sessions" value={data?.totals?.total_sessions ?? 0} tone="amber" testid="audit-total-sessions" />
      </div>

      {/* Top 3 callout */}
      {top.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="audit-top-3">
          {top.map((s, i) => (
            <div key={s.student_id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-white to-sky-50 dark:from-slate-800 dark:to-slate-900 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-sky-500">
                {["🥇 Most engaged", "🥈 Runner-up", "🥉 Third place"][i]}
              </div>
              <div className="text-lg font-bold text-slate-900 dark:text-slate-100 mt-1 truncate" style={{ fontFamily: "Outfit" }}>
                {s.student_name}
              </div>
              <div className="text-xs text-slate-400 font-mono">{s.roll_number} · Class {s.class_name}-{s.section}</div>
              <div className="mt-2 text-sm font-semibold text-emerald-600 dark:text-emerald-300">{fmtDuration(s.total_seconds)} active · {s.total_questions} questions</div>
            </div>
          ))}
        </div>
      )}

      {/* Activity heatmap — weekday × hour */}
      {heatmap && heatmap.total_minutes > 0 && <ActivityHeatmap hm={heatmap} onCellClick={openHeatCell} onLateNightClick={openLateNight} />}

      {/* Main table */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Per-student summary</div>
          <div className="flex items-center gap-2 flex-1 sm:flex-none sm:min-w-[260px]">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or roll…"
                className="pl-8 pr-8 h-9 rounded-full text-sm"
                data-testid="audit-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                  aria-label="Clear search"
                  data-testid="audit-search-clear"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-summary-table">
            <thead className="bg-slate-50 dark:bg-slate-900/40 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Student</th>
                <th className="text-left px-4 py-2.5">Class</th>
                <th className="text-right px-4 py-2.5">Active time</th>
                <th className="text-left px-4 py-2.5">Top subject</th>
                <th className="text-right px-4 py-2.5">Questions</th>
                <th className="text-right px-4 py-2.5">Sessions</th>
                <th className="text-right px-4 py-2.5">{/* drill */}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {filteredStudents.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center py-12 text-slate-400" data-testid="audit-empty">
                  {searchQuery
                    ? `No student matches "${searchQuery}". Try a different name or roll number.`
                    : "No tuition activity recorded for this filter. Students will appear here once they open the AI Tutor."}
                </td></tr>
              )}
              {filteredStudents.map((s) => (
                <tr key={s.student_id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors" data-testid={`audit-row-${s.student_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                      {s.student_name}
                      {(lateNightCounts[s.student_id] || 0) >= LATE_NIGHT_FLAG_THRESHOLD && (
                        <button
                          type="button"
                          onClick={() => openCheckin(s)}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-semibold uppercase tracking-wider hover:bg-amber-200 dark:hover:bg-amber-900/70 hover:ring-1 hover:ring-amber-400 transition-colors cursor-pointer"
                          title={`${lateNightCounts[s.student_id]} late-night sessions (10pm–5am) in this range — click to call the parent for a wellbeing check-in`}
                          data-testid={`late-night-flag-${s.student_id}`}
                        >
                          <AlertTriangle className="h-3 w-3" /> Check-in
                        </button>
                      )}
                      {checkinLog[s.student_id] && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold"
                          title={`Parent contacted ${fmtDate(checkinLog[s.student_id].last_at)}${checkinLog[s.student_id].by ? ` by ${checkinLog[s.student_id].by}` : ""}`}
                          data-testid={`checkin-done-${s.student_id}`}
                        >
                          <Phone className="h-3 w-3" /> Called {new Date(checkinLog[s.student_id].last_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400 font-mono">{s.roll_number}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{s.class_name}-{s.section}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600 dark:text-emerald-300">{fmtDuration(s.total_seconds)}</td>
                  <td className="px-4 py-3">
                    {s.subjects[0] ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-200 text-xs font-medium">
                        <BookOpen className="h-3 w-3" /> {s.subjects[0].subject_name}
                        <span className="text-slate-400 ml-0.5">· {fmtDuration(s.subjects[0].active_seconds)}</span>
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700 dark:text-slate-200">{s.total_questions}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700 dark:text-slate-200">{s.total_sessions}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openDrill(s)} className="rounded-full text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/30" data-testid={`audit-drill-${s.student_id}`}>
                      View sessions →
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <DrillDialog
        drillStudent={drillStudent}
        setDrillStudent={setDrillStudent}
        setDrillSessions={setDrillSessions}
        drillLoading={drillLoading}
        drillSessions={drillSessions}
      />

      <HeatmapDrillDialog
        hmDrill={hmDrill}
        setHmDrill={setHmDrill}
        setHmSessions={setHmSessions}
        hmDrillLoading={hmDrillLoading}
        hmSessions={hmSessions}
        exportHeatSlot={exportHeatSlot}
      />

      <CheckinDialog
        checkinTarget={checkinTarget}
        setCheckinTarget={setCheckinTarget}
        lateNightCounts={lateNightCounts}
        checkinMsg={checkinMsg}
        setCheckinMsg={setCheckinMsg}
        checkinSending={checkinSending}
        sendCheckin={sendCheckin}
      />
    </div>
    </Wrap>
  );
}
